/**
 * ============================================================================
 * DOCUMENTS STORE - UPDATE OPERATIONS
 * ============================================================================
 * 
 * Handles updating existing documents via the backend API.
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on failure
 * 
 * @module documents/api/update
 */

import { apiService } from "@/lib/api-functions/api-service";
import { documentStore } from "../store";
import type { DocumentItem, UpdateDocumentData, ToastFunction } from "../types";

// ============================================================================
// UPDATE OPERATIONS
// ============================================================================

/**
 * Update an existing document with local-first approach.
 * 
 * Updates local store immediately (optimistic update), then syncs to server.
 * Automatically rolls back on failure.
 * 
 * @param item - The document data to update (must include id)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the updated document
 * 
 * @example
 * ```ts
 * const updated = await updateDocumentItem({
 *   id: 'doc-123',
 *   game_id: 'game-456',
 *   name: 'Updated Document',
 *   data: [...],
 * });
 * ```
 */
export async function updateDocumentItem(
  item: UpdateDocumentData,
  toast?: ToastFunction
): Promise<DocumentItem> {
  // Store the current state for potential rollback
  const currentData = documentStore.getData();
  const existingItemIndex = currentData.findIndex(doc => doc.id === item.id);
  
  if (existingItemIndex === -1) {
    throw new Error('Document not found in local store');
  }
  
  // ========================================================================
  // OPTIMISTIC UPDATE
  // ========================================================================
  const updatedItem: DocumentItem = {
    ...currentData[existingItemIndex],
    ...item,
    updated_at: new Date().toISOString(),
  };
  
  // Update local store immediately for instant UI feedback
  const optimisticData = currentData.map(doc =>
    doc.id === item.id ? updatedItem : doc
  );
  documentStore.setData(optimisticData);

  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // SEND TO API
    // ======================================================================
    console.log('[updateDocumentItem] Sending to API:', JSON.stringify(item, null, 2));
    const result = await apiService.updateDocumentItem(item);
    console.log('[updateDocumentItem] API response:', JSON.stringify(result, null, 2));

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update document');
    }

    // ======================================================================
    // SYNC STORE WITH SERVER RESPONSE
    // ======================================================================
    const serverUpdatedItem = result.data;
    const finalData = documentStore.getData().map(doc =>
      doc.id === item.id ? serverUpdatedItem : doc
    );
    documentStore.setData(finalData);

    return serverUpdatedItem;
    
  } catch (err) {
    // ======================================================================
    // ROLLBACK ON FAILURE
    // ======================================================================
    documentStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to update document';
    console.error('[updateDocumentItem] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
